import React, { useEffect } from 'react';

// Style
import './AllDrugsVocabulary.scss';

// Lib
import clsx from 'clsx';
import { isEngLang } from 'lib/helpers';
import { shallowEqual } from 'react-redux';
import { useRegisterTutorialKey } from 'lib/hooks/useTutorial';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Components
import List from './components/List';
import NoData from 'components/NoData';
import Button from 'components/UiKit/Button';
import Loader from 'components/UiKit/Loader';
import Alphabet from './components/Alphabet';
import Breadcrumbs from 'components/Breadcrumbs';
import FieldText from 'components/UiKit/FieldText';
import ScrollButton from 'components/ScrollButton';

// Actions
import * as actionsCreator from './reducer';
import { showMessage } from 'reducers/messages';

export const AllDrugsVocabulary = () => {
	const dispatch = useAppDispatch();
	const registerTutorialKey = useRegisterTutorialKey();

	const {
		input,
		alphabet,
		abcWords,
		loadingABC,
		currentLetter
	} = useAppSelector(
		(store) => {
			return {
				input: store.allDrugsVocabulary.input,
				alphabet: store.allDrugsVocabulary.alphabet,
				abcWords: store.allDrugsVocabulary.abcWords,
				loadingABC: store.allDrugsVocabulary.loadingABC,
				currentLetter: store.allDrugsVocabulary.currentLetter
			};
		},
		shallowEqual
	);

	useEffect(() => {
		registerTutorialKey('drugs-impurities', () => !!abcWords?.length, "normal", "general");
	}, [abcWords]);

	useEffect(() => {
		dispatch(actionsCreator.getABC());
	}, [dispatch]);

	const data = !!input
		? abcWords.filter(
			(item) => {
				const isHasName = item?.drug && item.drug.toLowerCase().includes(input.toLowerCase());
				const isHasSynonyms = item?.synonyms?.length && item.synonyms?.some((item: string) => item.toLowerCase().includes(input.toLowerCase()));
				return isHasName || isHasSynonyms;
			}
		)
		: abcWords.filter(
			(item) => item.drug && item.drug[0] === currentLetter
		);

	const handlerChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		const isEng = isEngLang(value);
		if (!isEng) {
			dispatch(showMessage({
				type: 'info',
				text: 'Please use Latin letters only'
			}));
			return;
		}
		dispatch(actionsCreator.handlerChangeInput(value));
	};

	const handlerClear = () => {
		dispatch(actionsCreator.handlerChangeInput(''));
	};

	const getControl = () => {
		return (
			<div className="all-drugs-vocabulary__wrapper-control flex flex-ai-center flex-jc-start flex-direction-column-desktop mb30">
				<div className="all-drugs-vocabulary__wrapper-input flex flex-ai-center" id="all-drugs-vocabulary-search">
					<FieldText
						value={input}
						name="search"
						className="mr30"
						style={{ width: '34rem' }}
						placeholder="Search by name"
						onChange={handlerChangeInput}
					/>
					<Button
						isSvg
						variable="icon"
						icon="crossSvg"
						className="mr30"
						disabled={!input}
						title="Clear field"
						onPress={handlerClear}
					/>
				</div>
				<Alphabet
					data={alphabet}
					className="mr30"
					outerDisable={!!input}
					currentLetter={currentLetter}
				/>
			</div>
		);
	};

	const getLoader = () => {
		return <div className="all-drugs-vocabulary__loading flex flex-ai-center flex-jc-center">
			<Loader type="small" />
		</div>;
	};

	const getTitle = () => {
		return <>
			<h4 className="all-drugs-vocabulary__title mb10">
				Drugs and Impurities
			</h4>
			<p className={cnSubTitle}>
				Click on the corresponding letter to filter by name
			</p>
		</>;
	};

	const cnSubTitle = clsx(
		'all-drugs-vocabulary__sub-title mb10',
		loadingABC ? 'mb30' : ''
	);

	return (
		<div className="all-drugs-vocabulary relative">
			<ScrollButton />
			{/*<>{overlay}</>*/}
			<div className="wrapper">
				<Breadcrumbs className="mb10" />
				{getTitle()}
				{loadingABC
					? getLoader()
					: abcWords?.length
						? <>
							{getControl()}
							<List input={input} data={data} />
						</>
						: <NoData />}
			</div>
		</div>
	);
};

export default AllDrugsVocabulary;
